import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao, Cartao, Selo, classesBotao } from '../../componentes/base';
import { Carregando, Erro, Vazio } from '../../componentes/estados';
import { Entrada, EntradaSensivel, Rotulo, Selecao } from '../../componentes/formulario';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro, post } from '../../nucleo/api';
import { data, dataHora, email as emailFormatado, nomePessoa, numero, textoLivre } from '../../nucleo/formato';
import { podeNoWorkspace, useSessao } from '../../nucleo/sessao';
import { COR_ESTADO, type Acesso } from './CartaoPortal';

/** Acessos ao portal de todos os clientes do workspace (quem entra, papel, estado e último acesso). */
export function Acessos() {
  const { t } = useTranslation('portal');
  const tn = useTranslation().t;
  const s = useSessao();
  const [estado, setEstado] = useState('');
  const q = useQuery({ queryKey: ['portal', 'acessos', 'todos', estado], queryFn: () => get<{ itens: Acesso[] }>('/portal/acessos', estado ? { estado } : undefined) });
  return (
    <div className="flex flex-col gap-4">
      <div className="w-full max-w-xs">
        <Rotulo id="portal-filtro-estado" rotulo={t('lista.filtro')}>
          <Selecao
            id="portal-filtro-estado"
            value={estado}
            onChange={(e) => setEstado(e.target.value)}
            opcoes={[
              { valor: '', rotulo: tn('comum.todos') },
              { valor: 'ativo', rotulo: t('estado.ativo') },
              { valor: 'convidado', rotulo: t('estado.convidado') },
              { valor: 'revogado', rotulo: t('estado.revogado') },
            ]}
          />
        </Rotulo>
      </div>
      <Cartao semPadding>
        {q.isPending ? (
          <Carregando />
        ) : q.isError ? (
          <Erro erro={q.error} aoTentar={() => q.refetch()} />
        ) : !q.data.itens.length ? (
          <Vazio
            texto={t('lista.vazia')}
            acao={
              s.pode('cadastros.ver') ? (
                <Link to="/cadastros/cadastros/pessoas" className={classesBotao()}>
                  {t('lista.irParaClientes')}
                </Link>
              ) : undefined
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-portal-todos>
              <thead>
                <tr className="border-b border-borda text-left text-xs text-tinta-3">
                  <th className="px-4 py-2 font-medium">{t('colunas.cliente')}</th>
                  <th className="px-4 py-2 font-medium">{t('colunas.pessoa')}</th>
                  <th className="px-4 py-2 font-medium">{t('colunas.papel')}</th>
                  <th className="px-4 py-2 font-medium">{t('colunas.estado')}</th>
                  <th className="px-4 py-2 font-medium">{t('colunas.ultimo')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-borda">
                {q.data.itens.map((a) => (
                  <tr key={a.id}>
                    <td className="px-4 py-2">
                      <Link to={`/cadastros/cadastros/parceiro/${a.parceiro_id}`} className="hover:text-acento-forte">
                        {nomePessoa(a.parceiro_nome ?? '')}
                      </Link>
                    </td>
                    <td className="px-4 py-2">
                      <span className="block">{a.nome ? nomePessoa(a.nome) : '-'}</span>
                      <span className="block text-xs text-tinta-3">{emailFormatado(a.email)}</span>
                    </td>
                    <td className="px-4 py-2">{t(`papel.${a.papel}`)}</td>
                    <td className="px-4 py-2">
                      <span className="flex flex-wrap gap-1">
                        {a.equipe && <Selo cor="azul">{t('marcaEquipe')}</Selo>}
                        <Selo cor={COR_ESTADO[a.estado]}>{t(`estado.${a.estado}`)}</Selo>
                      </span>
                    </td>
                    <td className="numero px-4 py-2 text-tinta-2">{a.ultimo_acesso_em ? dataHora(a.ultimo_acesso_em) : t('nunca')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Cartao>
    </div>
  );
}

interface Termo {
  id: string;
  versao: string;
  titulo: string;
  texto: string;
  publicado_em: string;
  aceites: number;
}

/**
 * Trajetórias (19/10, item 5): prazo do modo saída (dias de Portal depois do último contrato encerrado), padrão do
 * workspace. Quem não configura vê o prazo vigente.
 */
function PrazoSaida() {
  const { t } = useTranslation('portal');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['portal', 'saida'], queryFn: () => get<{ dias: number; padrao: number; max: number }>('/portal/saida') });
  const [dias, setDias] = useState<string | null>(null);
  const valor = dias ?? (q.data ? String(q.data.dias) : '');
  const salvar = useMutation({
    mutationFn: () => api('PUT', '/portal/saida', { dias: Number(valor) }),
    onSuccess: async () => {
      toast.sucesso(t('config.saidaSalvo'));
      setDias(null);
      await qc.invalidateQueries({ queryKey: ['portal', 'saida'] });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const valido = /^\d{1,3}$/.test(valor) && Number(valor) >= 1 && Number(valor) <= (q.data?.max ?? 730);
  return (
    <Cartao titulo={t('config.saida')}>
      <p className="mb-3 text-sm text-tinta-2">{t('config.saidaTexto')}</p>
      {q.isPending ? (
        <Carregando />
      ) : q.isError ? (
        <Erro erro={q.error} aoTentar={() => q.refetch()} />
      ) : podeNoWorkspace(s, 'portal.configurar') ? (
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (valido) salvar.mutate();
          }}
        >
          <div className="min-w-0 grow basis-48">
            <Rotulo id="portal-saida-dias" rotulo={t('config.saidaDias')}>
              <Entrada id="portal-saida-dias" inputMode="numeric" maxLength={3} value={valor} onChange={(e) => setDias(e.target.value.replace(/\D/g, ''))} />
            </Rotulo>
          </div>
          <Botao type="submit" carregando={salvar.isPending} disabled={!valido || Number(valor) === q.data.dias}>
            {t('config.saidaSalvar')}
          </Botao>
        </form>
      ) : (
        <p className="text-sm text-tinta-1" data-portal-saida>
          {t('config.saidaAtual', { count: q.data.dias })}
        </p>
      )}
    </Cartao>
  );
}

/** Configuração do portal: o endereço e as versões do termo de uso (o cliente aceita a vigente). */
export function Configuracao() {
  const { t } = useTranslation('portal');
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const termos = useQuery({ queryKey: ['portal', 'termos'], queryFn: () => get<{ itens: Termo[] }>('/portal/termos') });
  const estado = useQuery({ queryKey: ['portal', 'estado'], queryFn: () => get<{ configurado: boolean; url: string | null }>('/portal/estado') });
  const [versao, setVersao] = useState('');
  const [titulo, setTitulo] = useState('');
  const [texto, setTexto] = useState('');
  const publicar = useMutation({
    mutationFn: () => post('/portal/termos', { versao: versao.trim(), titulo: titulo.trim(), texto: texto.trim() }),
    onSuccess: async () => {
      toast.sucesso(t('config.publicado'));
      setVersao('');
      setTitulo('');
      setTexto('');
      await qc.invalidateQueries({ queryKey: ['portal', 'termos'] });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Cartao titulo={t('config.endereco')}>
        {estado.data?.url ? (
          <a href={estado.data.url} target="_blank" rel="noopener noreferrer" className="numero text-sm hover:text-acento-forte">
            {estado.data.url}
          </a>
        ) : (
          <p className="text-sm text-tinta-2">{t('config.naoConfigurado')}</p>
        )}
      </Cartao>
      <Cartao titulo={t('config.termos')}>
        <p className="mb-3 text-sm text-tinta-2">{t('config.termosTexto')}</p>
        {termos.isPending ? (
          <Carregando />
        ) : termos.isError ? (
          <Erro erro={termos.error} aoTentar={() => termos.refetch()} />
        ) : !termos.data.itens.length ? (
          <p className="text-sm text-laranja">{t('config.semTermo')}</p>
        ) : (
          <ul className="divide-y divide-borda" data-portal-termos>
            {termos.data.itens.map((tm, i) => (
              <li key={tm.id} className="flex items-center gap-3 py-2 text-sm">
                <div className="min-w-0 flex-1">
                  <TextoCortado como="p" className="font-medium">
                    {textoLivre(tm.titulo)}
                  </TextoCortado>
                  <p className="text-xs text-tinta-3">
                    {t('config.versao')} {tm.versao} · {data(tm.publicado_em)} · {t('config.aceites', { count: tm.aceites, n: numero(tm.aceites) })}
                  </p>
                </div>
                {i === 0 && <Selo cor="verde">{t('config.vigente')}</Selo>}
              </li>
            ))}
          </ul>
        )}
      </Cartao>
      <PrazoSaida />
      {podeNoWorkspace(s, 'portal.configurar') && (
        <Cartao titulo={t('config.nova')} className="lg:col-span-2">
          <form
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              publicar.mutate();
            }}
          >
            <div className="grid gap-4 sm:grid-cols-3">
              <Rotulo id="termo-versao" rotulo={t('config.versao')}>
                <EntradaSensivel id="termo-versao" mascara={null} livre rotulo={t('config.versao')} value={versao} maxLength={40} onChange={(e) => setVersao(e.target.value)} />
              </Rotulo>
              <div className="sm:col-span-2">
                <Rotulo id="termo-titulo" rotulo={t('config.titulo')}>
                  <EntradaSensivel id="termo-titulo" mascara={null} livre rotulo={t('config.titulo')} value={titulo} maxLength={200} onChange={(e) => setTitulo(e.target.value)} />
                </Rotulo>
              </div>
            </div>
            <Rotulo id="termo-texto" rotulo={t('config.texto')}>
              <textarea id="termo-texto" rows={10} value={texto} onChange={(e) => setTexto(e.target.value)} className="w-full rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1" />
            </Rotulo>
            <Botao type="submit" variante="primario" className="w-fit" carregando={publicar.isPending} disabled={!versao.trim() || !titulo.trim() || !texto.trim()}>
              {t('config.publicar')}
            </Botao>
          </form>
        </Cartao>
      )}
    </div>
  );
}
